import { baseApi, PaginatedResponse } from 'sama-shared';
import type { AdminOrderListItem, Order } from '../types/order';
import type { GetAdminOrdersParams } from '../types/api';

export const adminOrdersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminOrders: builder.query<PaginatedResponse<AdminOrderListItem>, GetAdminOrdersParams>({
      query: (params) => ({
        url: '/admin/order',
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        result && result.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'AdminOrder' as const, id })),
              { type: 'AdminOrder', id: 'LIST' },
            ]
          : [{ type: 'AdminOrder', id: 'LIST' }],
    }),

    getAdminOrderById: builder.query<Order, string>({
      query: (id) => ({
        url: `/admin/order/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'AdminOrder', id }],
    }),

    /**
     * No es una escritura de campo: el backend corre la lógica del flujo de
     * órdenes dentro de una transacción (ADMIN_ORDER_STATUS_UPDATE.md). Pasar a
     * DELIVERED libera liquidaciones; cancelar o marcar pago fallido las anula.
     * Por eso invalida también los settlements: quedaron viejos en la misma
     * operación, no en una posterior.
     */
    updateAdminOrderStatus: builder.mutation<Order, { id: string; status: number }>({
      query: ({ id, status }) => ({
        url: `/admin/order/${id}`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'AdminOrder', id },
        { type: 'AdminOrder', id: 'LIST' },
        'Settlement',
        'PartnerCollectSettlement',
        'DeliverySettlement',
        'OrderPayment',
      ],
    }),

    /** Cancela contablemente la orden, así que arrastra los mismos settlements. */
    deleteAdminOrder: builder.mutation<{ message: string; data: Partial<Order> }, string>({
      query: (id) => ({
        url: `/admin/order/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'AdminOrder', id },
        { type: 'AdminOrder', id: 'LIST' },
        'Settlement',
        'PartnerCollectSettlement',
        'DeliverySettlement',
      ],
    }),

    verifyAdminOrder: builder.mutation<{ message: string; data: { orderId: string } }, string>({
      query: (id) => ({
        url: `/admin/order/verify/${id}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'AdminOrder', id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdminOrdersQuery,
  useGetAdminOrderByIdQuery,
  useUpdateAdminOrderStatusMutation,
  useDeleteAdminOrderMutation,
  useVerifyAdminOrderMutation,
} = adminOrdersApi;
